<template>
  <div class="row">
    <div class="col-xl-6 d-flex">
      <div class="row flex-fill">
        <!-- KPI Cards -->
        <div class="col-sm-6">
          <div class="card bg-linear-gradiant border-white border-2 overlay-bg-3 position-relative">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div>
                  <p class="fw-medium mb-1">Total Deals</p>
                  <h5>{{ fmtNum(kpi.total_deals) }}</h5>
                </div>
                <div class="avatar avatar-md br-10 icon-rotate bg-primary">
                  <span class="d-flex align-items-center"><i class="ti ti-delta text-white fs-16"></i></span>
                </div>
              </div>
              <p class="fw-medium fs-13 text-muted mb-0">Based on selected filters</p>
            </div>
          </div>

          <div class="card bg-linear-gradiant border-white border-2 overlay-bg-3 position-relative">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div>
                  <p class="fw-medium mb-1">Deal Value</p>
                  <h5>{{ fmtMoney(kpi.total_value, kpi.currency) }}</h5>
                </div>
                <div class="avatar avatar-md br-10 icon-rotate bg-secondary">
                  <span class="d-flex align-items-center"><i class="ti ti-currency text-white fs-16"></i></span>
                </div>
              </div>
              <p class="fw-medium fs-13 text-muted mb-0">Sum of deal amounts</p>
            </div>
          </div>

          <div class="card bg-linear-gradiant border-white border-2 overlay-bg-3 position-relative">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div>
                  <p class="fw-medium mb-1">Won Deals</p>
                  <h5>{{ fmtNum(kpi.won_deals) }}</h5>
                </div>
                <div class="avatar avatar-md br-10 icon-rotate bg-pink">
                  <span class="d-flex align-items-center"><i class="ti ti-stairs-up text-white fs-16"></i></span>
                </div>
              </div>
              <p class="fw-medium fs-13 text-muted mb-0">Deals with status Won</p>
            </div>
          </div>
        </div>

        <div class="col-sm-6">
          <div class="card bg-linear-gradiant border-white border-2 overlay-bg-3 position-relative">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div>
                  <p class="fw-medium mb-1">Open Deals</p>
                  <h5>{{ fmtNum(kpi.open_deals) }}</h5>
                </div>
                <div class="avatar avatar-md br-10 icon-rotate bg-purple">
                  <span class="d-flex align-items-center"><i class="ti ti-users-group text-white fs-16"></i></span>
                </div>
              </div>
              <p class="fw-medium fs-13 text-muted mb-0">Deals with status Open</p>
            </div>
          </div>

          <div class="card bg-linear-gradiant border-white border-2 overlay-bg-3 position-relative">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div>
                  <p class="fw-medium mb-1">Lost Deals</p>
                  <h5>{{ fmtNum(kpi.lost_deals) }}</h5>
                </div>
                <div class="avatar avatar-md br-10 icon-rotate bg-info">
                  <span class="d-flex align-items-center"><i class="ti ti-swipe text-white fs-16"></i></span>
                </div>
              </div>
              <p class="fw-medium fs-13 text-muted mb-0">Deals with status Lost</p>
            </div>
          </div>

          <div class="card bg-linear-gradiant border-white border-2 overlay-bg-3 position-relative">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
                <div>
                  <p class="fw-medium mb-1">Avg Deal Size</p>
                  <h5>{{ fmtMoney(kpi.avg_deal_size, kpi.currency) }}</h5>
                </div>
                <div class="avatar avatar-md br-10 icon-rotate bg-warning">
                  <span class="d-flex align-items-center"><i class="ti ti-star text-white fs-16"></i></span>
                </div>
              </div>
              <p class="fw-medium fs-13 text-muted mb-0">Total / count</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Funnel -->
    <div class="col-xl-6 d-flex">
      <div class="card flex-fill">
        <div class="card-header">
          <div class="d-flex align-items-center justify-content-between">
            <h5>Pipeline Stages</h5>
            <small class="text-muted">Click a bar to filter stage</small>
          </div>
        </div>

        <div class="card-body">
          <div id="pipeline_chart">
            <apexchart
              type="bar"
              height="280"
              :options="pipelineOptions"
              :series="pipelineSeries"
            />
          </div>

          <div class="mt-3">
            <h6 class="mb-3">Leads Values By Stages</h6>
            <div class="row g-2 justify-content-center">
              <div v-for="x in funnelList" :key="x.stage" class="col-md col-sm-4 col-6">
                <div class="border rounded text-center p-2 hover-pointer" @click="drillStage(x.stage)">
                  <p class="mb-1">
                    <i class="ti ti-point-filled text-primary"></i>{{ x.stage }}
                  </p>
                  <h6>{{ fmtMoney(x.value, kpi.currency) }}</h6>
                  <small class="text-muted">{{ fmtNum(x.count) }} deals</small>
                </div>
              </div>

              <div v-if="!funnelList.length" class="text-muted small">
                No funnel data for selected filters.
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useDealDashboardStore } from "@/stores/dealDashboardStore";
import * as Toast from "@/utils/toast";

const dash = useDealDashboardStore();

const toastInfo = (msg) => {
  if (Toast?.toast?.info) return Toast.toast.info(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "info");
  console.log(msg);
};

const kpi = computed(() => ({
  total_deals: dash.kpis?.total_deals ?? 0,
  total_value: dash.kpis?.total_value ?? 0,
  won_deals: dash.kpis?.won_deals ?? 0,
  open_deals: dash.kpis?.open_deals ?? 0,
  lost_deals: dash.kpis?.lost_deals ?? 0,
  avg_deal_size: dash.kpis?.avg_deal_size ?? 0,
  currency: dash.kpis?.currency ?? "KES",
}));

const funnelList = computed(() =>
  (dash.pipelineFunnel || []).map((x) => ({
    stage: x.stage ?? x.stage_name ?? x.name ?? "Stage",
    value: Number(x.value ?? 0),
    count: Number(x.count ?? 0),
  }))
);

const pipelineSeries = computed(() => [
  {
    name: "Value",
    data: funnelList.value.map((x) => Number(x.value ?? 0)),
  },
]);

const pipelineOptions = computed(() => ({
  chart: {
    type: "bar",
    height: 280,
    events: {
      dataPointSelection: async (event, chartContext, config) => {
        const idx = config?.dataPointIndex ?? -1;
        const stage = funnelList.value[idx]?.stage;
        if (stage) await drillStage(stage);
      },
    },
  },
  plotOptions: {
    bar: {
      borderRadius: 0,
      horizontal: true,
      distributed: true,
      barHeight: "80%",
      isFunnel: true,
    },
  },
  dataLabels: { enabled: true },
  xaxis: {
    categories: funnelList.value.map((x) => `${x.stage} : ${x.count}`),
    labels: { style: { colors: "#6B7280", fontSize: "13px" } },
  },
  grid: { borderColor: "#E5E7EB", strokeDashArray: 5 },
  legend: { show: false },
}));

function fmtNum(v) {
  const n = Number(v || 0);
  return n.toLocaleString();
}
function fmtMoney(v, cur = "KES") {
  const n = Number(v || 0);
  return `${cur} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

async function drillStage(stageName) {
  const ok = dash.setStageByName(stageName);
  if (!ok) {
    toastInfo(`Stage "${stageName}" not mapped to an ID. Select pipeline first, then click again.`);
    return;
  }
  await dash.fetchDashboard();
}
</script>

<style scoped>
.hover-pointer { cursor: pointer; }
</style>
